import { useToast } from '@components/index'
import { apiClient } from '@services/api-client'
import { useOnError } from '@services/utils/onError'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { RentModel } from 'types/api'

export type OfferPaymentType = 'RENT' | 'DEPOSIT'
export type FlatPaymentType = 'LANDLORD_RENT' | 'LANDLORD_DEPOSIT'

/**
 * A payment created by hand: the client's (`offer`) or SNA's to the landlord
 * (`flat`). An empty variable symbol is filled in by the backend.
 */
export type RentCreateInput = (
  | { offer: string; payment_type: OfferPaymentType }
  | { flat: string; payment_type: FlatPaymentType }
) & {
  rent: number
  /** Rents only. */
  monthly_advance_prescription?: number
  due_date: string
  variable_symbol?: string
  /** Required for rents (YYYYMM), ignored for deposits. */
  specific_symbol?: string
  description?: string
  paid?: boolean
  paid_date?: string | null
}

export function useRentCreate() {
  const toast = useToast()
  const { t } = useTranslation('toast')
  const queryClient = useQueryClient()
  const onError = useOnError()

  async function createRent(body: RentCreateInput) {
    const res = await apiClient.post<RentModel>('/api/v1/rents/', body)
    return res.data
  }

  return useMutation({
    mutationFn: createRent,
    onSuccess: () => {
      toast.success({ message: t('paymentCreated') })
      queryClient.invalidateQueries({ queryKey: ['rents'] })
      queryClient.invalidateQueries({ queryKey: ['flats'] })
    },
    onError
  })
}
